import { EmeraldError } from 'src/common/api/emerald-error';
import { getManualVolume } from 'src/manual-volume/business-logic/handle-manual-volume';
import { validateVolume } from '../../common/helpers/volume-uom-validate';
import { validateWeight } from '../../common/helpers/weight-uom-validate';
import { IItemLookup } from '../../common/interfaces/dtos/IItemLookup';
import { IQuantity } from '../../common/interfaces/dtos/IQuantity';
import { IItemInputData } from '../../common/interfaces/IItemInputData';
import { getWeightIfStable } from '../../devices/scale-operations';
import { validateItemQuantityByUom } from '../../home-page/business-logic/common/item-quantity-validator';
import { lookupItemById } from './item-lookup';
import { ILineItemAddData } from './line-item-add';
import { getLineItem } from './line-item-builder';

export interface IItemBuiltQuery {
  inputData: IItemInputData;
  itemId?: string;
  itemLookup?: IItemLookup;
  isWeightEmbeddedInBarcode?: boolean;
  action?: string;
  quantity?: IQuantity;
}

export const queryAndBuildLineItem = async ({
  inputData,
  itemId,
  itemLookup,
  isWeightEmbeddedInBarcode,
  action,
  quantity,
}: IItemBuiltQuery): Promise<ILineItemAddData> => {
  if (!itemId) {
    if (inputData.itemId) {
      itemId = inputData.itemId;
    } else {
      throw new EmeraldError('SpecifyItemCode', 'Please specify item code');
    }
  }

  itemLookup =
    itemLookup || (await lookupItemById({ data: itemId }, inputData.entryMethod, action || 'Sell'));

  validateItemQuantityByUom(
    inputData.quantity,
    itemLookup?.unitsOfMeasure,
    inputData.isManualWeight,
  );

  //TODO: When giftCard is implemented, check if the item is gift card and validate it.
  const { Units, UnitOfMeasureCode } = await getQuantity(
    inputData,
    itemLookup,
    isWeightEmbeddedInBarcode,
    action,
    quantity,
  );
  const lineItem = getLineItem(inputData, UnitOfMeasureCode, Units);

  return { lineItem, itemLookup, inputData };
};

const emptyIQuantity: IQuantity = {};
const getQuantity = async (
  inputData: IItemInputData,
  itemLookup: IItemLookup,
  isWeightEmbeddedInBarcode?: boolean,
  action: string = 'Sell',
  quantity?: IQuantity,
): Promise<IQuantity> => {
  if (!quantity) {
    if (action === 'x:Resubmit') {
      // quantity = handleResubmit();
    } else {
      quantity = await getWeightQuantityIfWeighable(
        inputData,
        itemLookup,
        isWeightEmbeddedInBarcode,
      );
    }
  }
  return quantity || emptyIQuantity;
};

const getWeightQuantityIfWeighable = async (
  inputData: IItemInputData,
  itemLookup: IItemLookup,
  isWeightEmbeddedInBarcode?: boolean,
): Promise<IQuantity> => {
  const unitsOfMeasure = itemLookup.unitsOfMeasure;
  const quantity: IQuantity = {
    Value: 0,
    Units: inputData.quantity,
    UnitOfMeasureCode: unitsOfMeasure,
  };
  const packQuantity = itemLookup?.packQuantity?.units;
  if (packQuantity) {
    if (itemLookup.packQuantity?.unitOfMeasureCode) {
      // its amount embedded or RTC amount embedded, needs EA and 1 unit
      quantity.Units = 1;
      quantity.UnitOfMeasureCode = 'EA';
      quantity.Value = 1;
    } else {
      quantity.Units = packQuantity;
    }
    return quantity;
  }
  if (isWeightEmbeddedInBarcode) {
    return quantity;
  }
  if (validateWeight(unitsOfMeasure)) {
    return tryGetWeightQuantity(inputData, itemLookup);
  }
  if (validateVolume(unitsOfMeasure)) {
    const volumeQuantity = {
      UnitOfMeasureCode: itemLookup.unitsOfMeasure,
      Units: await getManualVolume(itemLookup, inputData),
    };
    return volumeQuantity;
  }
  return quantity;
};

const tryGetWeightQuantity = async (
  inputData: IItemInputData,
  itemLookup: IItemLookup,
): Promise<IQuantity> => {
  let weightQuantity: IQuantity = {};
  if (inputData.isManualWeight) {
    weightQuantity = {
      UnitOfMeasureCode: itemLookup.unitsOfMeasure,
      Units: inputData.quantity,
      Value: inputData.quantity,
    };
  } else {
    weightQuantity = {
      UnitOfMeasureCode: itemLookup.unitsOfMeasure,
      Units: await getWeightIfStable(itemLookup.itemId, itemLookup.description),
    };
  }
  return weightQuantity;
};

// const handleResubmit = (): IQuantity {
//   TODO: When scale is implemented rewrite this logic
//   var scaleDataModel = GetModelInspector<IScaleWeightModelInspector>();

//           var nullDecimalWeight = scaleDataModel.DecimalWeight;

//   const newQuantity: IQuantity = {
//     EntryMethod = scaleDataModel.EntryMethod,
//     UnitOfMeasureCode = scaleDataModel.UnitOfMeasureCode,
//     Value = scaleDataModel.Value,
//     Units = nullDecimalWeight.HasValue ? nullDecimalWeight.Value : 0
//   };
//   return newQuantity;
// }
